<template>
    <div>
        <div class="d-flex align-center flex-wrap">
            <daterange
                :start-date-label="$t('date_from')"
                :end-date-label="$t('date_to')"
                :_start-date="dateFrom"
                :_end-date="dateTo"
                same-date
                :single-line="false"
                :hide-details="false"
                class="flex-grow-0"
                :end-date-days-offset="730"
                content-class="date-field flex-grow-0"
                @change-start-date="changeStartDate"
                @change-end-date="changeEndDate"
            />
            <v-radio-group v-model="availabilityTableViewMode" inline>
                <v-radio value="AVAILABLE" :label="$t('actual_availability')" />
                <v-radio value="ALL">
                    <template v-slot:label>
                        <div class="text-no-wrap">
                            <span class="text-capitalize">{{ $t('all') }}</span>
                            &nbsp;(<span>{{ $t('allocated') }}</span>
                            &nbsp;/&nbsp;
                            <span class="text-primary">{{ $t('booked') }}</span>
                            &nbsp;/&nbsp;
                            <span class="text-success">{{ $t('available') }}</span
                            >)
                        </div>
                    </template>
                </v-radio>
            </v-radio-group>
        </div>
        <v-tabs v-model="tab" :mandatory="false" color="primary">
            <v-tab v-for="type in massEditTypes" :key="type" :value="type">
                {{ $t(`mass_edit_type.${type}`) }}
            </v-tab>
        </v-tabs>
        <v-tabs-window v-model="tab">
            <v-window-item v-for="type in massEditTypes" :key="type" :value="type">
                <AvailabilityMassEditForm
                    :mode="type"
                    :date-from="dateFrom"
                    :date-to="dateTo"
                    :availability="availability"
                    :contract="contract"
                    class="mt-3"
                    :sub-allotments="subAllotments"
                    :data-cy="`product-availability-mass-edit-form-${type}`"
                    @update="loadAvailability"
                    @create-suballotment="loadSubAllotments"
                />
            </v-window-item>
        </v-tabs-window>
        <v-skeleton-loader
            v-if="availabilityLoading || subAllotmentsLoading"
            transition="fade-transition"
            type="table"
        />
        <template v-else>
            <AvailabilityTableLegend class="my-5 float-right" />
            <AvailabilityTable
                v-if="allotmentInventory && hasAllotments(allotmentInventory)"
                :inventory="allotmentInventory"
                :dates="dates"
                :date-from="dateFrom"
                :date-to="dateTo"
                :sub-allotments="subAllotments"
                :contract="contract"
            >
                <template v-slot:release>
                    <tr>
                        <td class="td-header text-caption pl-2">{{ $t('release') }}</td>
                        <td v-for="day in dates" :key="`${day}`" :class="dateCellCSSClass(day)" class="text-center">
                            <template v-if="gatAvailability(allotmentInventory.availabilityType, day, 'NETTO')">
                                {{ gatAvailability(allotmentInventory.availabilityType, day, 'NETTO').release }}
                                {{
                                    $t(
                                        'day',
                                        gatAvailability(allotmentInventory.availabilityType, day, 'NETTO').release
                                    )
                                }}
                            </template>
                        </td>
                    </tr>
                </template>
            </AvailabilityTable>
            <AvailabilityTable
                v-if="onRequestInventory && hasAllotments(onRequestInventory)"
                :inventory="onRequestInventory"
                :dates="dates"
                :date-from="dateFrom"
                :date-to="dateTo"
                :sub-allotments="subAllotments"
                :contract="contract"
            />
        </template>
    </div>
</template>

<script setup lang="ts">
    import Daterange from '~src/components/search/forms/Daterange.vue'
    import AvailabilityTable from './AvailabilityTable.vue'
    import AvailabilityMassEditForm from './AvailabilityMassEditForm.vue'
    import {gatAvailability} from '~src/utils/availability'
    import AvailabilityTableLegend from '~src/components/products/AvailabilityTableLegend.vue'
    import {contractServiceType} from '~src/utils/types/types'
    import {nextTick} from 'vue'

    const {$api} = useNuxtApp()

    const {contract} = defineProps<{
        contract: any
    }>()

    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)
    const dateFrom = ref(tomorrow)
    const dateTo = ref(new Date())
    dateTo.value.setMonth(dateTo.value.getMonth() + 1)

    const availability = ref()
    const availabilityLoading = ref(true)

    const tab = ref('none')
    const $route = useRoute()

    const subAllotments = ref()
    const subAllotmentsLoading = ref(true)

    onMounted(async () => {
        // Ensure dateFrom is set to tomorrow after component mount
        await nextTick()
        const tomorrow = new Date()
        tomorrow.setDate(tomorrow.getDate() + 1)
        dateFrom.value = tomorrow

        await loadAvailability()
        await loadSubAllotments()
    })

    async function loadAvailability(): Promise<void> {
        try {
            availabilityLoading.value = true
            availability.value = await $api.availability.get({
                contractId: contract.id || $route.params?.contractId,
                dateFrom: dateFrom.value,
                dateTo: dateTo.value,
            })
        } catch (error) {
            availability.value = null
        } finally {
            availabilityLoading.value = false
        }
    }

    async function loadSubAllotments(): Promise<void> {
        try {
            subAllotmentsLoading.value = true
            subAllotments.value = await $api.subAllotments.get({
                contractId: contract.id || $route.params?.contractId,
                active: true,
            })
        } catch (error) {
            subAllotments.value = null
        } finally {
            subAllotmentsLoading.value = false
        }
    }

    const dates = computed(() => {
        const dates = []
        const date = new Date(dateFrom.value)
        while (date <= dateTo.value) {
            dates.push(new Date(date))
            date.setDate(date.getDate() + 1)
        }
        return dates
    })

    const massEditTypes = computed(() => {
        if (contract.serviceType === contractServiceType.ownVisa) {
            return []
        }

        return ['mass_edit']
    })

    function changeStartDate($event: Date) {
        dateFrom.value = $event
        loadAvailability()
    }

    function changeEndDate($event: Date) {
        dateTo.value = $event
        loadAvailability()
    }

    const allotmentInventory = computed(() => availability.value?.inventories.find(({name}) => name === 'Allotment'))
    const onRequestInventory = computed(() => availability.value?.inventories.find(({name}) => name === 'On Request'))

    function hasAllotments(inventory: any) {
        return (
            hasAnyAvailability(inventory.availabilityType, dates.value, 'NETTO') ||
            hasAnyAvailability(inventory.availabilityType, dates.value, 'FREE_SALE') ||
            hasAnyAvailability(inventory.availabilityType, dates.value, 'CUSTOMER')
        )
    }

    function hasAnyAvailability(availabilityType: any, dates: Date[], allotmentType: string) {
        return !!dates.find((date: Date) =>
            gatAvailability(availabilityType, date, allotmentType) !== null
        )
    }

    const availabilityTableViewMode = ref<'AVAILABLE' | 'ALL'>('ALL')
    provide('availabilityTableViewMode', availabilityTableViewMode)

    function dateCellCSSClass(day: Date) {
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        day.setHours(0, 0, 0, 0)

        if (day.getTime() === today.getTime()) {
            return 'bg-blue-lighten-5'
        }
        return ''
    }
</script>

<style scoped lang="scss">
    .date-field {
        max-width: 200px;
    }
</style>
